import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { X, Star, BookOpen, Heart, Trophy, Sun, Smile, Zap, Award } from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";

const AVATARS = [
  { id: "star", icon: Star, color: "#FFD166", bg: "#FFF9E0", label: "Star" },
  { id: "book", icon: BookOpen, color: "#118AB2", bg: "#E0F4FF", label: "Scholar" },
  { id: "heart", icon: Heart, color: "#EF476F", bg: "#FFE0EB", label: "Heart" },
  { id: "trophy", icon: Trophy, color: "#F77F00", bg: "#FFE9CC", label: "Champ" },
  { id: "sun", icon: Sun, color: "#FF9F1C", bg: "#FFF3CD", label: "Sunny" },
  { id: "smile", icon: Smile, color: "#06D6A0", bg: "#E0FFF5", label: "Happy" },
  { id: "zap", icon: Zap, color: "#9B5DE5", bg: "#F0E6FF", label: "Spark" },
  { id: "award", icon: Award, color: "#4CC9F0", bg: "#E0F7FF", label: "Hero" },
];

export default function AddChildModal({ editChild, onClose, onSaved }) {
  const { t } = useLanguage();
  const [name, setName] = useState(editChild?.name || "");
  const [age, setAge] = useState(editChild?.age || "");
  const [avatar, setAvatar] = useState(editChild?.avatar || "star");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !age) return;
    if (age < 3 || age > 18) {
      setError(t('age_must_be_3_18'));
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (editChild) {
        await axios.put(`${API}/children/${editChild.id}`, { name: name.trim(), age: parseInt(age), avatar });
      } else {
        await axios.post(`${API}/children`, { name: name.trim(), age: parseInt(age), avatar });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || t('could_not_save_child'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="add-child-modal">
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
            {editChild ? t('edit_child') : t('add_new_child')}
          </h2>
          <button
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
            data-testid="close-modal-btn"
          >
            <X size={20} color="#6B7280" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">{t('childs_name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#118AB2] transition-colors font-medium text-gray-800"
              placeholder={t('childs_name_placeholder')}
              required
              data-testid="child-name-input"
            />
          </div>

          {/* Age */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">{t('age_3_18')}</label>
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#118AB2] transition-colors font-medium text-gray-800"
              placeholder={t('enter_age')}
              min={3}
              max={18}
              required
              data-testid="child-age-input"
            />
            {age && (
              <p className="text-xs font-semibold mt-1" style={{ 
                color: parseInt(age) <= 7 ? "#FFD166" : parseInt(age) <= 12 ? "#118AB2" : "#9B5DE5" 
              }}>
                {parseInt(age) <= 7 
                  ? `${t('little_light')} (${t('ages')} 3-7)` 
                  : parseInt(age) <= 12 
                    ? `${t('bible_explorer')} (${t('ages')} 8-12)` 
                    : `${t('faith_builder')} (${t('ages')} 13-18)`}
              </p>
            )}
          </div>

          {/* Avatar selection */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">{t('choose_avatar')}</label>
            <div className="grid grid-cols-4 gap-2">
              {AVATARS.map((av) => {
                const Icon = av.icon;
                const selected = avatar === av.id;
                return (
                  <button
                    key={av.id}
                    type="button"
                    onClick={() => setAvatar(av.id)}
                    className={`aspect-square rounded-2xl flex flex-col items-center justify-center p-2 transition-all border-2 ${selected ? "border-[#118AB2] shadow-md scale-110" : "border-transparent bg-gray-50 hover:bg-gray-100"}`}
                    style={{ backgroundColor: selected ? av.bg : undefined }}
                    data-testid={`avatar-${av.id}`}
                  >
                    <Icon size={24} color={selected ? av.color : "#9CA3AF"} strokeWidth={2.5} />
                    <span className="text-xs font-bold mt-1" style={{ color: selected ? av.color : "#9CA3AF" }}>
                      {av.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm font-medium" data-testid="child-form-error">{error}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold py-3 rounded-xl transition-all"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#06D6A0] hover:bg-[#05b588] text-white font-black py-3 rounded-xl border-b-2 border-[#049a73] active:border-b-0 transition-all disabled:opacity-60"
              style={{ fontFamily: "Fredoka, sans-serif" }}
              data-testid="save-child-btn"
            >
              {loading ? t('saving') : editChild ? t('save_changes') : t('add_child_btn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
